/**
 * Copyright Aquera Inc 2023
 *
 * This source code is licensed under the BSD-3-Clause license found in the
 * LICENSE file in the root directory of this source tree.
 */

import {
  LitElement,
  html,
  CSSResultArray,
  TemplateResult,
} from 'lit';
import { customElement, state, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { styles } from './nile-breadcrumb-item.css';
import NileElement from '../internal/nile-element';
/**
 * Nile BreadCrumb Item component.
 *
 * @tag nile-breadcrumb-item
 *
 */
@customElement('nile-breadcrumb-item')
export class NileBreadcrumbItem extends NileElement {
  /**
   * The styles for BreadcrumbItem
   * @remarks If you are extending this class you can extend the base styles with super. Eg `return [super(), myCustomStyles]`
   */
  public static get styles(): CSSResultArray {
    return [styles];
  }

  @state() private isLast = false;

  /* #endregion */

  /* #region Methods */

  /**
   * Render method
   * @slot This is a slot test
   */

  @property({ type: String }) separator = 'var(--nile-icon-arrow-right, var(--ng-icon-chevron-right))';

  private handleClick() {
    if (this.isLast) {
      return;
    }
    this.emit('nile-click-item');
  }

  public render(): TemplateResult {
    return html`
      <slot
        class=${classMap({
          'nile-breadcrumb-item__slot-text': !this.isLast,
          'nile-breadcrumb-item__last-slot-text': this.isLast,
        })}
        @click=${this.handleClick}
      ></slot>
      <nile-icon
        name=${this.separator}
        aria-label=${this.separator}
        method="var(--nile-svg-method-fill, var(--ng-svg-method-stroke))"
        color="var(--nile-colors-dark-500, var(--ng-colors-fg-quaternary-400))"
        class=${classMap({
          'nile-breadcrumb-item__arrow': true,
          'nile-breadcrumb-item__arrow-hidden': this.isLast,
        })}
        size="14"
      ></nile-icon>
    `;
  }

  /* #endregion */
}

export default NileBreadcrumbItem;

declare global {
  interface HTMLElementTagNameMap {
    'nile-breadcrumb-item': NileBreadcrumbItem;
  }
}
